/**
 * packages/server/src/index.ts
 *
 * Gencow Server Runtime — Pure framework, no app-specific code.
 *
 * App functions are loaded dynamically from GENCOW_FUNCTIONS env var.
 * This is how Convex works: the runtime runs your convex/ functions.
 *
 * Usage:
 *   GENCOW_FUNCTIONS=../../apps/tests/e2e/gencow PORT=3456 tsx src/index.ts
 */
import { Hono } from "hono";
import { cors } from "hono/cors";
import { logger } from "hono/logger";

import { resolve } from "path";
import { existsSync, readFileSync } from "fs";
import { createDatabase } from "./database";
import { applyRuntimeEnvSnapshot, parseRuntimeEnvContent } from "./env-runtime.js";
import { setupRuntimeEnvHotReload } from "./server-env-runtime-bootstrap.js";
import {
  loadAppModule,
  loadAuthAdapterSchema,
  loadAuthConfig,
  loadProjectConfig,
  resolveFunctionsPath,
} from "./server-bootstrap.js";
import { executeWithTimeout } from "./server-execution.js";
import { readShutdownReasonFromMarker, startFallbackServer } from "./server-lifecycle.js";
import {
  ensurePlatformSchemaUpgrade,
  ensureOAuthExchangeSystemSchema,
  ensureRagSystemSchema,
  ensureWorkflowSystemSchema,
} from "./server-platform-db-bootstrap.js";
import { bootstrapPlatformRuntime } from "./server-platform-runtime-bootstrap.js";
import { bootstrapServerMainRuntime } from "./server-main-runtime.js";
import { createAnalyticsSqlRuntime } from "./server-analytics-db.js";
import { createServerWebsocketRuntime } from "./server-websocket-runtime.js";

import { parseArgs } from "@gencow/core";
import { initAuth, betterAuthMiddleware, betterAuthHandler } from "./better-auth";
import { createRuntimeStorage } from "./storage.js";
import { createSettingsRoutes, captureEnvSnapshot, loadDbEnvVars, sanitizeSystemEnvVars } from "./settings";
import { getSecretKey } from "./crypto";
import { createPlatformFailoverClients, RuntimeBuffer } from "./platform-failover";
import { loadPlatformRuntimeConfigFile } from "./server-platform-config-file";
import { createAdminAccessPolicy, resolveAdminSurfaceAccess } from "./server-admin-access.js";
import { createPlatformSystemDb } from "./server-platform-rls.js";
import { resolveCorsOrigin, resolveOriginPolicy } from "./server-origin-policy.js";
import { resolveServerRuntimeMode } from "./server-runtime-mode.js";
import {
  checkPlatformSpendCapForRequest,
  createPlatformSpendCapCache,
  shouldMeterPlatformRequestPath,
} from "./server-spend-cap";
import { meterResponseBandwidth } from "./response-bandwidth-meter.js";

// ─── BaaS Usage Metering (Layer 2 — Runtime 불변 코드) ────
//
// BaaS 배포 시 Platform이 GENCOW_METERING_URL + GENCOW_INTERNAL_TOKEN을 주입.
// 로컬 dev에서는 metering 비활성.

loadPlatformRuntimeConfigFile({ logger: console });

const METERING_URL = process.env.GENCOW_METERING_URL;
const METERING_URL_ALT = process.env.GENCOW_METERING_URL_ALT;
const RUNTIME_MODE = resolveServerRuntimeMode();
const IS_BAAS = RUNTIME_MODE.isBaas;
const IS_PLATFORM = RUNTIME_MODE.isPlatform;
const INTERNAL_TOKEN = process.env.GENCOW_INTERNAL_TOKEN || "";
const APP_NAME = process.env.GENCOW_APP_NAME || "";
const APP_DB_ID = process.env.GENCOW_APP_DB_ID || "";
const WAKE_SIGNAL_ENV = {
  GENCOW_WAKE_SIGNAL_ENABLED: process.env.GENCOW_WAKE_SIGNAL_ENABLED,
  GENCOW_WAKE_SIGNAL_DATABASE_URL: process.env.GENCOW_WAKE_SIGNAL_DATABASE_URL,
};

// WS Gateway: BaaS 모드에서 realtime.emit() / refresh() → Platform Gateway HTTP callback
// GENCOW_METERING_URL이 있으면 BaaS 모드 → Platform이 로컸호스트에 있으므로 METERING_URL 재사용
const PLATFORM_INTERNAL_URL = process.env.GENCOW_PLATFORM_INTERNAL_URL || METERING_URL;
const PLATFORM_INTERNAL_URL_ALT = process.env.GENCOW_PLATFORM_INTERNAL_URL_ALT || METERING_URL_ALT;

const { fetchPlatformInternal, notifyGateway } = createPlatformFailoverClients({
  platformInternalUrl: PLATFORM_INTERNAL_URL,
  platformInternalUrlAlt: PLATFORM_INTERNAL_URL_ALT,
  internalToken: INTERNAL_TOKEN,
  appName: APP_NAME,
});

const runtimeBuffer = new RuntimeBuffer();
const platformSpendCapCache = createPlatformSpendCapCache();
/** WS 접속 시작 시각 기록 */
const wsTimers = new WeakMap<object, number>();

let bunServer: any = null; // set after Bun.serve()

// ─── App ─────────────────────────────────────────────────

const app = new Hono();
const { upgradeWebSocket, websocket } = createServerWebsocketRuntime();
let runtimeProjectConfig: Awaited<ReturnType<typeof loadProjectConfig>> | undefined;

// ─── Middleware ───────────────────────────────────────────

/** 플랫폼 기본 도메인 — 환경변수 1회 읽기. CORS + 프록시 MW에서 재사용 */
const PLATFORM_DOMAIN = process.env.BASE_DOMAIN || "gencow.app";

function resolveRuntimeOriginPolicy() {
  return resolveOriginPolicy({
    baseDomain: PLATFORM_DOMAIN,
    appPublicDomain: process.env.APP_PUBLIC_DOMAIN,
    capturedBaseURL: process.env.BETTER_AUTH_URL,
    projectTrustedOrigins: runtimeProjectConfig?.trustedOrigins,
    corsOriginsEnv: process.env.CORS_ORIGINS,
  });
}

app.use(
  "*",
  cors({
    origin: (origin) => {
      return resolveCorsOrigin(origin, resolveRuntimeOriginPolicy());
    },
    credentials: true,
  }),
);
// ── HTTP 로그 — 인프라 경로 기본 suppress ──
// /_admin/*, /_dashboard/*, /ws, /api/auth/** 는 개발 중 노이즈.
// GENCOW_VERBOSE=true 또는 --verbose 시 전체 로그 활성화.
const VERBOSE = process.env.GENCOW_VERBOSE === "true";
const SUPPRESS_PREFIXES = ["/_admin", "/_dashboard", "/api/auth"];

app.use("*", async (c, next) => {
  const path = c.req.path;
  if (!VERBOSE && (path === "/ws" || SUPPRESS_PREFIXES.some((p) => path.startsWith(p)))) {
    return next(); // 로그 없이 통과
  }
  // Hono logger 실행
  return logger()(c, next);
});

// ── x-gencow-platform 식별 헤더 ──
// 커스텀 도메인 HTTP 검증용 (Cloudflare 프록시 등에서 DNS 직접 매칭 불가 시)
app.use("*", async (c, next) => {
  await next();
  c.header("x-gencow-platform", "1");
});

// ── BaaS 통합 Metering 미들웨어 ────────────────────────────
// API 호출 카운트, 응답 시간, Bandwidth를 미들웨어에서 측정.
// 401 early return시에도 카운트되도록 핸들러 밖에서 측정.
if (IS_BAAS) {
  app.use("*", async (c, next) => {
    const path = c.req.path;
    const isApiPath = shouldMeterPlatformRequestPath(path);
    if (!isApiPath) {
      return next();
    }
    const spendCap = await checkPlatformSpendCapForRequest({
      internalToken: INTERNAL_TOKEN,
      appName: APP_NAME,
      appDbId: APP_DB_ID,
      cache: platformSpendCapCache,
      fetchPlatformInternal,
      logger: console,
    });
    if (!spendCap.allowed) {
      return c.json(
        {
          error: "Platform spend cap exceeded",
          code: "platform_spend_cap_exceeded",
          remaining: spendCap.remaining ?? 0,
        },
        402,
      );
    }
    const start = Date.now();
    await next();
    runtimeBuffer.add("api_calls", 1);
    runtimeBuffer.add("function_ms", Date.now() - start);
    // Bandwidth (모든 비인프라 경로): body를 clone/read 하지 않고 통과 chunk만 계측.
    try {
      c.res = meterResponseBandwidth(c.res, (bytes) => runtimeBuffer.add("bandwidth_bytes", bytes));
    } catch {
      // metering 실패는 무시 (metering 누락 > 앱 중단)
    }
  });
}
// better-auth 미들웨어 + 라우트 핸들러는 DB 초기화 후 main()에서 등록.
// ⚠️ IS_PLATFORM 모드에서 프록시 미들웨어보다 뒤에 등록해야 함 (서브도메인 요청이 앱 서버로 전달되도록).

// ─── Bootstrap ──────────────────────────────────────────

async function main() {
  const _mainStart = performance.now();
  const isWarmWorker = process.env.GENCOW_WARM_WORKER === "true";
  const _t = (label: string) => {
    if (isWarmWorker) {
      console.log(`[warm-timing] ${label}: ${(performance.now() - _mainStart).toFixed(0)}ms`);
    }
  };
  // ── 1. Load app functions ─────────────────────────────
  //
  // GENCOW_FUNCTIONS points to the app's gencow/ folder (like convex/).
  // Importing it registers all query/mutation handlers and injects db.
  //
  const functionsPath = resolveFunctionsPath();

  console.log(`[gencow] Loading functions from: ${functionsPath}`);
  const appModule = await loadAppModule({
    functionsPath,
    isWarmWorker,
    onTiming: _t,
  });

  // ── 2. Initialize DB (⭐ Promise.all 병렬화) ─────────────────
  //
  // Database 초기화와 auth config import를 병렬로 실행.
  // 두 작업은 완전히 독립적이므로 안전.
  //
  _t("pre-db");

  // ⭐ 병렬 실행: DB 초기화 + auth/project config import (독립적 작업)
  const [dbResult, authConfig, projectConfig] = await Promise.all([
    // DB 초기화 (PgBouncer 연결 ~10ms)
    createDatabase(),
    loadAuthConfig(functionsPath),
    loadProjectConfig(functionsPath),
  ]);
  const authSchema = await loadAuthAdapterSchema(functionsPath, projectConfig, {
    isPlatform: IS_PLATFORM,
  });
  runtimeProjectConfig = projectConfig;

  const { db, systemDb, rawSql, systemRawSql, migrate, close, driver } = dbResult;
  const platformSystemDb = createPlatformSystemDb(db, { isPlatform: IS_PLATFORM });
  _t("post-db");

  const migrationsFolder = process.env.GENCOW_MIGRATIONS
    ? resolve(process.cwd(), process.env.GENCOW_MIGRATIONS)
    : resolve(process.cwd(), "../../migrations");

  if (process.env.GENCOW_SKIP_MIGRATION === "true") {
    console.log("[db] migrations skipped (GENCOW_SKIP_MIGRATION=true)");
  } else {
    try {
      await migrate(migrationsFolder);
      console.log("[db] migrations applied ✓");
    } catch (e: any) {
      const msg: string = e?.message ?? String(e);
      console.error("[db] ❌ Migration failed:", msg);
      console.error("[db] ── Run: gencow db:reset (자동 백업 후 리셋)");
      console.error("[db] ── ⚠️ rm -rf 하지 마세요! 데이터가 영구 삭제됩니다.");
      // BaaS 앱은 fail-fast, 로컬 dev는 fallback 서버로 대기
      if (IS_BAAS) process.exit(1);
      throw new Error(`Migration failed: ${msg}`);
    }
  }

  // ── 2c. Workflow system tables — app DB durable execution state ──
  // BaaS에서는 provisioner가 superuser로 선행 생성한다.
  // 앱 런타임(gencow_app_user)은 DML only 이므로 부팅 시 DDL을 시도하면 permission denied가 발생한다.
  await ensureWorkflowSystemSchema({
    rawSql: rawSql as (query: string, params?: unknown[]) => Promise<unknown>,
    isBaas: IS_BAAS,
    logger: console,
  });
  await ensureOAuthExchangeSystemSchema({
    rawSql: rawSql as (query: string, params?: unknown[]) => Promise<unknown>,
    isBaas: IS_BAAS,
    logger: console,
  });
  await ensureRagSystemSchema({
    rawSql: rawSql as (query: string, params?: unknown[]) => Promise<unknown>,
    isBaas: IS_BAAS,
    logger: console,
  });

  // ── 2b. Schema upgrade — 플랫폼 전용 테이블 컬럼 추가 (기존 DB 호환) ──
  // 앱 프로세스에는 apps/deployments/usage_snapshots 테이블이 없으므로 skip
  await ensurePlatformSchemaUpgrade({
    rawSql: rawSql as (query: string, params?: unknown[]) => Promise<unknown>,
    isPlatform: IS_PLATFORM,
    logger: console,
  });

  // ── 3. Initialize Storage ─────────────────────────────
  const storageDir = process.env.GENCOW_STORAGE || "./uploads";
  // 스토리지 쿼터: GENCOW_STORAGE_QUOTA (MB 단위, 기본 1024MB = 1GB, 0 = 무제한)
  const storageQuotaMB = Number(process.env.GENCOW_STORAGE_QUOTA || "1024");
  const storageQuota = storageQuotaMB > 0 ? storageQuotaMB * 1024 * 1024 : 0;
  const storage = createRuntimeStorage({
    mode: IS_BAAS ? "platform" : "local",
    storageDir,
    rawSql,
    storageQuotaBytes: storageQuota,
    appName: APP_NAME,
    readGrantSecret: INTERNAL_TOKEN,
  });

  // ── 3-1. Initialize Auth (⭐ Lazy Loading) ───────────────
  //
  // auth config는 위 Promise.all에서 이미 로드됨.
  // initAuth()는 config만 저장 (betterAuth() 호출 안 함).
  // 실제 better-auth 초기화는 첫 auth 요청 시 ensureAuth()에서 실행.
  //
  initAuth(platformSystemDb, authConfig, projectConfig, { authSchema });
  app.use("*", betterAuthMiddleware(platformSystemDb));
  console.log("[auth] better-auth configured (lazy — loads on first auth request) ✓");
  _t("post-auth");

  // ── 3-2. Initialize Settings (Environment Variables) ─────
  //
  // 1. .env에서 로드된 환경변수를 스냅샷 (DB 변수와 구분 용도)
  // 2. secret.key 준비 (없으면 자동 생성)
  // 3. DB에 저장된 환경변수를 process.env에 주입 (.env 값 덮어쓰지 않음)
  //
  captureEnvSnapshot(); // .env 원본 보존
  getSecretKey(); // 암호화 키 준비
  const adminSurfaceAccess = resolveAdminSurfaceAccess({ isBaas: IS_BAAS, isPlatform: IS_PLATFORM });
  const settingsRoutes = createSettingsRoutes(rawSql, {
    accessPolicy: createAdminAccessPolicy(adminSurfaceAccess),
  });

  const loadedCount = await loadDbEnvVars(rawSql);
  if (loadedCount > 0) {
    console.log(`[settings] Loaded ${loadedCount} env var(s) from DB ✓`);
  }

  const analyticsSqlRuntime = await createAnalyticsSqlRuntime({
    fallbackRawSql: systemRawSql,
    logger: console,
  });

  const platformEmailEnv = {
    RESEND_API_KEY: process.env.RESEND_API_KEY,
    PLATFORM_EMAIL_FROM: process.env.PLATFORM_EMAIL_FROM,
    PLATFORM_EMAIL_WORKER_DISABLED: process.env.PLATFORM_EMAIL_WORKER_DISABLED,
    PLATFORM_EMAIL_WORKER_INTERVAL_MS: process.env.PLATFORM_EMAIL_WORKER_INTERVAL_MS,
    PLATFORM_EMAIL_WORKER_BATCH_SIZE: process.env.PLATFORM_EMAIL_WORKER_BATCH_SIZE,
    PLATFORM_EMAIL_WORKER_MAX_ATTEMPTS: process.env.PLATFORM_EMAIL_WORKER_MAX_ATTEMPTS,
  };
  const platformWebhookEnv = {
    PLATFORM_WEBHOOK_WORKER_DISABLED: process.env.PLATFORM_WEBHOOK_WORKER_DISABLED,
    PLATFORM_WEBHOOK_WORKER_INTERVAL_MS: process.env.PLATFORM_WEBHOOK_WORKER_INTERVAL_MS,
    PLATFORM_WEBHOOK_WORKER_BATCH_SIZE: process.env.PLATFORM_WEBHOOK_WORKER_BATCH_SIZE,
    PLATFORM_WEBHOOK_WORKER_MAX_ATTEMPTS: process.env.PLATFORM_WEBHOOK_WORKER_MAX_ATTEMPTS,
    PLATFORM_WEBHOOK_WORKER_TIMEOUT_MS: process.env.PLATFORM_WEBHOOK_WORKER_TIMEOUT_MS,
  };
  const realtimeOutboxEnv = {
    GENCOW_REALTIME_OUTBOX_POLL_INTERVAL_MS: process.env.GENCOW_REALTIME_OUTBOX_POLL_INTERVAL_MS,
  };
  const workflowReconcilerEnv = {
    GENCOW_WORKFLOW_RECONCILER_INTERVAL_MS: process.env.GENCOW_WORKFLOW_RECONCILER_INTERVAL_MS,
  };

  // ── 3-2b. Sanitize system env vars (Security) ─────────
  //
  // DB 연결, Auth 초기화, Settings 로드가 모두 완료된 후
  // process.env에서 시스템 변수를 삭제하여 사용자 코드 접근 차단.
  // 이 시점 이후 사용자 함수의 handler()가 호출되어도
  // process.env.DATABASE_URL 등에 접근할 수 없다.
  //
  sanitizeSystemEnvVars();

  // ── 3-2c. .env.runtime Hot-Reload (BaaS mode) ─────────
  //
  // Platform provisioner가 .env.runtime 파일에 사용자 환경변수를 기록.
  // fs.watch(inotify)로 파일 변경을 즉시 감지하여 process.env에 merge.
  // → gencow env set 후 앱 재시작 없이 즉시 반영.
  //
  if (IS_BAAS) {
    await setupRuntimeEnvHotReload({
      functionsPath: process.env.GENCOW_FUNCTIONS || "",
      internalToken: process.env.GENCOW_INTERNAL_TOKEN || "",
      isWarmWorker,
      logger: console,
      env: process.env,
      existsSyncImpl: existsSync,
      readFileSyncImpl: readFileSync,
      parseRuntimeEnvContentImpl: parseRuntimeEnvContent,
      applyRuntimeEnvSnapshotImpl: applyRuntimeEnvSnapshot,
    });
  }

  // ── 3-3. Subdomain Proxy (Platform mode only) ─────────
  //
  // IS_PLATFORM=true 시 {app}.{BASE_DOMAIN} → localhost:{port} 프록시.
  // DB의 apps 테이블에서 name→port 매핑을 조회.
  //
  if (IS_PLATFORM) {
    await bootstrapPlatformRuntime({
      app,
      db: platformSystemDb,
      rawSql: systemRawSql as (query: string, params?: unknown[]) => Promise<unknown>,
      analyticsRawSql: analyticsSqlRuntime.rawSql as (query: string, params?: unknown[]) => Promise<unknown>,
      functionsPath,
      platformDomain: PLATFORM_DOMAIN,
      getBunServer: () => bunServer,
      logger: console,
      fetchImpl: fetch,
      setIntervalImpl: setInterval,
      clearIntervalImpl: clearInterval,
      setTimeoutImpl: setTimeout,
      platformEmailEnv,
      platformWebhookEnv,
      wakeSignalEnv: WAKE_SIGNAL_ENV,
    });
  }

  // ─── Auth Routes (better-auth) ─────────────────────────────
  //
  // ⚠️ 반드시 프록시 미들웨어 뒤에 등록해야 함.
  // 모듈 스코프에 등록하면 서브도메인의 /api/auth/* 요청을 플랫폼이 가로채서
  // 플랫폼 키로 JWT를 발급함 → 앱 서버에서 키 불일치로 401 발생.
  // see: docs/analysis/analysis-jwt-auth-failure.md
  //
  app.all("/api/auth/*", betterAuthHandler);

  _t("pre-serve");
  bunServer = await bootstrapServerMainRuntime({
    app,
    appModule,
    upgradeWebSocket,
    websocket,
    db,
    systemDb,
    platformSystemDb,
    rawSql,
    analyticsRawSql: analyticsSqlRuntime.rawSql,
    close: async () => {
      await analyticsSqlRuntime.close();
      await close();
    },
    driver,
    storage,
    storageDir,
    settingsRoutes,
    functionsPath,
    authConfig,
    projectConfig,
    isBaas: IS_BAAS,
    isPlatform: IS_PLATFORM,
    appName: process.env.GENCOW_APP_NAME || "",
    internalToken: INTERNAL_TOKEN,
    executeWithTimeout,
    parseArgs,
    runtimeBuffer,
    fetchPlatformInternal,
    notifyGateway,
    readShutdownReasonFromMarker,
    wsTimers,
    wakeSignalEnv: WAKE_SIGNAL_ENV,
    realtimeOutboxEnv,
    workflowReconcilerEnv,
    logger: console,
  });
  _t("post-serve");
}

// ─── AppType export (for Hono RPC client) ────────────────
export type AppType = typeof app;

// ─── Warm Worker 지원 ─────────────────────────────────
// GENCOW_WARM_WORKER=true이면 main() 자동실행 skip.
// warm-process-entry.ts에서 import 후 start() 직접 호출.
export { main };
export async function start(): Promise<ReturnType<typeof Bun.serve>> {
  await main();
  return bunServer;
}

if (process.env.GENCOW_WARM_WORKER !== "true") {
  main().catch((bootError) =>
    startFallbackServer({
      bootError,
      isBaas: IS_BAAS,
      isPlatform: IS_PLATFORM,
    }),
  );
}
